import { inject, Injectable, OnDestroy } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { CommonDataService } from '@sb-tools/typescript/services/common-data.service';
import { UserDataService } from '@sb-tools/typescript/services/user-data.service';
import { CommonDataForSettingsService } from '@core/services/common-data-for-settings.service';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { CustomerConfigurationService } from '@core/services/customer-configuration.service';
import { FaqWidgetService } from '@sb-tools/typescript/services/faq-widget.service';
import { LocalizationLanguage } from '@sb-tools/typescript/enums/localization-language.enum';

@Injectable({
    providedIn: 'root',
})
class PermissionsService implements OnDestroy {
    constructor(
        private userDataService: UserDataService,
        private commonDataService: CommonDataService,
        private commonDataForSettingsService: CommonDataForSettingsService,
        private translateService: TranslateService,
        private customerConfigurationService: CustomerConfigurationService,
        private router: Router,
        private faqWidgetService: FaqWidgetService,
    ) {}

    private unsubscriber = new Subject<void>();

    canActivate(): Observable<boolean> {
        return forkJoin({
            user: this.userDataService.getCurrentUser(),
            customerSettings: this.customerConfigurationService.getAdminCustomerInstance(),
        }).pipe(
            takeUntil(this.unsubscriber),
            map((data) => {
                this.commonDataService.user = data.user;
                this.commonDataForSettingsService.communityUser = { ...data.user };
                this.commonDataService.communityName = data.customerSettings.name;
                if (data.user.localizationLanguage !== LocalizationLanguage.english) {
                    this.translateService.use(getSetupLanguage(data.user.localizationLanguage));
                }

                this.commonDataForSettingsService.customerSettings.next(data.customerSettings);

                if (
                    data.user.localizationLanguage === LocalizationLanguage.norwegian ||
                    data.customerSettings.language === LocalizationLanguage.norwegian
                ) {
                    this.faqWidgetService.init();
                }
                return true;
            }),
            catchError(() => {
                this.router.navigate(['500'], { skipLocationChange: true });
                return of(false);
            }),
        );
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}

export const AuthenticatedUserGuard: CanActivateFn = (): Observable<boolean> => {
    return inject(PermissionsService).canActivate();
};

export const getSetupLanguage = (language) => {
    switch (language) {
        case LocalizationLanguage.norwegian:
            return 'nb';
        case LocalizationLanguage.swedish:
            return 'sv_SE';
        case LocalizationLanguage.danish:
            return 'da_DK';
        case LocalizationLanguage.finnish:
            return 'fi';
        case LocalizationLanguage.english:
            return 'en';
        default:
            return undefined;
    }
};
